import { NativeImage } from "@/components/native/NativeImage";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { useImageGallery } from "./useImageGallery";
import GalleryControls from "./GalleryControls";
import GalleryThumbnails from "./GalleryThumbnails";
import GalleryIndicators from "./GalleryIndicators";
import FullscreenGallery from "./FullscreenGallery";
import { Loader2 } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useEffect, useMemo, useState } from "react";

interface ImageGalleryProps {
  images: string[];
  /** Optional separate URLs for fullscreen (e.g. full-size originals). Must match `images` length/order. */
  fullscreenImages?: string[];
  title: string;
  onImageChange?: (index: number) => void;
  /**
   * Optional override: open image in parent lightbox (e.g. DocumentView Dialog)
   * instead of using FullscreenGallery overlay.
   */
  onOpenImage?: (args: { url: string; alt: string; index: number }) => void;
}

const ImageGallery = ({
  images,
  fullscreenImages,
  title,
  onImageChange,
  onOpenImage,
}: ImageGalleryProps) => {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);
  const viewFullSizeLabel =
    labels.gallery?.viewFullSize ?? "Visa i full storlek";
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const {
    currentImageIndex,
    setCurrentImageIndex,
    showFullscreen,
    setShowFullscreen,
    navigateImage,
    touchHandlers,
    imageRef,
  } = useImageGallery({ images });

  // Sync Embla carousel selected index with state (mobile swipe-follows-finger)
  useEffect(() => {
    if (!carouselApi || !isMobile || images.length <= 1) return;
    const onSelect = () => {
      const idx = carouselApi.selectedScrollSnap();
      setCurrentImageIndex(idx);
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi, isMobile, images.length, setCurrentImageIndex]);

  // When currentImageIndex is set externally (e.g. thumbnails), scroll carousel on mobile
  useEffect(() => {
    if (!isMobile || !carouselApi || images.length <= 1) return;
    if (carouselApi.selectedScrollSnap() !== currentImageIndex) {
      carouselApi.scrollTo(currentImageIndex);
    }
  }, [isMobile, carouselApi, currentImageIndex, images.length]);

  // Stable key: only changes when the actual list of URLs changes (e.g. different listing),
  // not when parent re-renders and passes a new array reference with same content.
  // This prevents clearing loaded state when e.g. Skador re-renders due to onImageChange.
  const imagesSignature = useMemo(
    () => (images.length ? images.join("\0") : ""),
    [images],
  );

  // Reset loaded images only when the image set actually changes (e.g. switching listing)
  useEffect(() => {
    setLoadedImages(new Set());
    setIsLoading(true);
  }, [imagesSignature]);

  // Reset loading state when image changes, but only if image hasn't been loaded before
  useEffect(() => {
    if (!loadedImages.has(currentImageIndex)) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [currentImageIndex, loadedImages]);

  // Call onImageChange when currentImageIndex changes
  useEffect(() => {
    if (onImageChange) {
      onImageChange(currentImageIndex);
    }
  }, [currentImageIndex, onImageChange]);

  const useCarouselSwipe = isMobile && images.length > 1;
  const effectiveFullscreenImages =
    fullscreenImages && fullscreenImages.length === images.length
      ? fullscreenImages
      : images;

  return (
    <div className="relative">
      <div
        className="relative aspect-video"
        ref={imageRef}
        onTouchStart={
          useCarouselSwipe ? undefined : touchHandlers.handleTouchStart
        }
        onTouchMove={
          useCarouselSwipe ? undefined : touchHandlers.handleTouchMove
        }
        onTouchEnd={useCarouselSwipe ? undefined : touchHandlers.handleTouchEnd}
      >
        {/* Loading indicator – only for single-image view (carousel shows slides in DOM) */}
        {!useCarouselSwipe && isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 z-10 rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {useCarouselSwipe ? (
          /* Mobile: Embla carousel so image follows finger when swiping (same as CarImageGallery) */
          <Carousel
            setApi={setCarouselApi}
            opts={{ align: "start", loop: false, dragFree: false }}
            className="h-full w-full [&_.overflow-hidden]:!h-full rounded-lg"
          >
            <CarouselContent className="h-full ml-0 pl-0">
              {images.map((url, index) => (
                <CarouselItem
                  key={`img-${index}`}
                  className="pl-0 basis-full flex-shrink-0 flex-grow-0"
                >
                  <div
                    className="w-full h-full flex items-center justify-center bg-black rounded-lg cursor-pointer"
                    onClick={() => {
                      setCurrentImageIndex(index);
                      if (onOpenImage) {
                        onOpenImage({
                          url: effectiveFullscreenImages[index],
                          alt: `${title} ${index + 1}`,
                          index,
                        });
                        return;
                      }
                      setShowFullscreen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setCurrentImageIndex(index);
                        if (onOpenImage) {
                          onOpenImage({
                            url: effectiveFullscreenImages[index],
                            alt: `${title} ${index + 1}`,
                            index,
                          });
                          return;
                        }
                        setShowFullscreen(true);
                      }
                    }}
                    aria-label={viewFullSizeLabel}
                  >
                    <NativeImage
                      src={url}
                      alt={`${title} ${index + 1}`}
                      className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg select-none touch-none pointer-events-none"
                      draggable={false}
                      loading="eager"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-black rounded-lg cursor-pointer"
            onClick={() => {
              if (onOpenImage) {
                onOpenImage({
                  url: effectiveFullscreenImages[currentImageIndex],
                  alt: `${title} ${currentImageIndex + 1}`,
                  index: currentImageIndex,
                });
                return;
              }
              setShowFullscreen(true);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (onOpenImage) {
                  onOpenImage({
                    url: effectiveFullscreenImages[currentImageIndex],
                    alt: `${title} ${currentImageIndex + 1}`,
                    index: currentImageIndex,
                  });
                  return;
                }
                setShowFullscreen(true);
              }
            }}
            aria-label={viewFullSizeLabel}
          >
            <NativeImage
              src={images[currentImageIndex]}
              alt={`${title} ${currentImageIndex + 1}`}
              className={`max-w-full max-h-full w-auto h-auto object-contain rounded-lg transition-opacity duration-200 pointer-events-none ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={() => {
                setIsLoading(false);
                setLoadedImages((prev) => new Set(prev).add(currentImageIndex));
              }}
              onError={() => {
                setIsLoading(false);
                setLoadedImages((prev) => new Set(prev).add(currentImageIndex));
              }}
            />
          </div>
        )}

        <GalleryControls
          imagesLength={images.length}
          onPrevious={() => navigateImage("prev")}
          onNext={() => navigateImage("next")}
          onFullscreen={onOpenImage ? undefined : () => setShowFullscreen(true)}
        />
      </div>

      {/* Image indicators for mobile */}
      {isMobile && (
        <GalleryIndicators
          count={images.length}
          currentIndex={currentImageIndex}
          onSelect={setCurrentImageIndex}
        />
      )}

      {/* Thumbnails - only show on larger screens */}
      {!isMobile && (
        <GalleryThumbnails
          images={images}
          currentIndex={currentImageIndex}
          onSelect={setCurrentImageIndex}
          title={title}
        />
      )}

      {/* Fullscreen view (disabled when parent lightbox handles open) */}
      {!onOpenImage && (
        <FullscreenGallery
          images={effectiveFullscreenImages}
          title={title}
          currentIndex={currentImageIndex}
          open={showFullscreen}
          onOpenChange={setShowFullscreen}
          onNavigate={navigateImage}
          onSelect={setCurrentImageIndex}
        />
      )}
    </div>
  );
};

export default ImageGallery;
